import { assertUnreachable, parseJson } from "@arti/utilities";

// ---------------------------------------------------------------------------
// Blackboard — instance & channel models
// ---------------------------------------------------------------------------

export type InstanceStatus = "running" | "waiting" | "killed";
export type InFlightStepKind =
  | "actor_wake"
  | "consolidation_only_wake"
  | "external_compact_seed";
export type InFlightWakeState =
  | "preparing"
  | "lock_waiting"
  | "llm_call"
  | "between_steps";
export type ReasoningEffort =
  | "none"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max";

export interface ActorAttribution {
  avatarSeed: string;
  displayName: string;
  id: string;
}

export interface BlackboardInstance {
  id: string;
  isOptimisticSpawn?: boolean;
  organizationId: string;
  displayName: string;
  status: InstanceStatus;
  currentRunId: string | null;
  modelRef: string | null;
  identityPrompt: string | null;
  tickIntervalMs: number | null;
  spawnedBy: string | null;
  templateId: string | null;
  avatarSeed: string;
  channels: BlackboardChannel[];
  tokenUsage: {
    inputTokens: number;
    outputTokens: number;
    cachedInputTokens: number;
    cacheWriteTokens: number;
  };
  /** How many compact→prepare evolution cycles this actor has completed. 0 = never compacted. */
  evolution: number;
  /**
   * Context window metrics — persisted from last activation, shown for all statuses.
   * Null only when the actor has never run (no AMR record yet).
   */
  contextWindow: {
    /** Total tokens in the current context window. */
    contextWindowSize: number;
    /** Tokens accumulated in the act phase since last evolution cycle. */
    contextGrowthSinceEvolution: number;
    /** Tokens remaining before the phase budget triggers compact. */
    phaseBudgetRemaining: number;
    /** Current phase (act / prepare / compact). */
    currentPhase: string;
  } | null;
  /** Number of distinct run records. 1 = healthy cycling actor; >1 = restarts/instability. */
  runCount: number;
  /** Accumulated cost across all runs for this actor instance, in USD. Null when not yet available. */
  totalCostUsd: number | null;
  /**
   * Cache stats from the most recent completed activation. Updated live via WS cache_stats events.
   * Null until the first activation completes.
   */
  lastCacheStats: {
    /** Cache hit percentage for this activation (0–100), or null if no input tokens. */
    cacheHitPct: number | null;
    /** True if cache was completely cold this activation (non-first, non-zero input, zero cache reads). */
    isBreak: boolean;
  } | null;
  /** ISO timestamp of last park. Null if the actor has never parked. Used for cooldown countdown. */
  lastParkedAt: string | null;
  /** Provider outage/backoff gate. Non-null + future means pending wake input remains unread. */
  wakeBlockedAt: string | null;
  wakeBlockedUntil: string | null;
  wakeBlockedReason: string | null;
  wakeBlockedError: string | null;
  wakeBlockedTriggerChannelMessageId: string | null;
  wakeBlockedConsecutiveFailureCount: number | null;
  /**
   * ISO timestamp of the most recent `step_kind='actor_wake'` step start.
   * Filtered to actor wakes (excludes consolidation/cache_warm/compact_seed) so
   * the thermal warm/cold badge tracks act-phase prompt-cache liveness, not
   * any wake's park time. Null until the first actor wake completes.
   */
  lastActorWakeAt: string | null;
  /**
   * Actor-wake-only lifetime cache stats. `cacheHitPct` is Formula B
   * (`cache_read / (cache_read + no_cache)`) — same definition as the per-wake
   * `cache_stats` WS event; the lifetime and per-activation numbers now share
   * one metric. Null until the first actor_wake step lands.
   */
  actorWakeCacheStats: {
    cacheHitPct: number | null;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    noCacheTokens: number;
  } | null;
  /**
   * True when this actor is blocked on the per-channel in-flight mutex.
   * Set by `channel_lock_waiting` WS events and cleared by `channel_lock_acquired`.
   * Independent of `status`: an actor can be both `status="running"` and queued
   * behind the lock — the UI should render a distinct QUEUED badge in that case.
   */
  channelLockWaiting: boolean;
  inFlightStepKind: InFlightStepKind | null;
  inFlightWakeState: InFlightWakeState | null;
  /**
   * Most recent acquire wait time in ms. Useful for surfacing contention cost
   * Even after the lock was acquired. Null if never contended.
   */
  lastChannelLockWaitMs: number | null;
  /**
   * ISO timestamp when the identity prompt was last saved. If this is after
   * lastParkedAt, the system prompt cache will break on the next activation.
   */
  systemPromptEditedAt: string | null;
  /**
   * Effective settings and raw overrides for the compact threshold + autonomic
   * cadence. Effective values are what the runtime uses; overrides are what the
   * instance row carries (null = inherit template). Populated from the API.
   */
  overrides: {
    compactContextGrowthThreshold: number;
    compactContextGrowthThresholdOverride: number | null;
    compactContextGrowthThresholdSource: "instance" | "template";
    heartbeatIntervalS: number | null;
    heartbeatIntervalSOverride: number | null;
    heartbeatIntervalSSource: "instance" | "template" | null;
    consolidationSchedule: string | null;
    consolidationScheduleOverride: string | null;
    consolidationScheduleSource: "instance" | "template" | null;
    reasoningEffort: ReasoningEffort | null;
    reasoningEffortOverride: ReasoningEffort | null;
    reasoningEffortSource: "instance" | "template" | null;
    /**
     * Org MCP servers this actor reaches. `mcpServerIds` is the resolved set;
     * the override is what the instance row carries, where null inherits the
     * template and [] means "none, even though the template attaches some".
     */
    mcpServerIds: string[];
    mcpServerIdsOverride: string[] | null;
    mcpServerIdsSource: "instance" | "template" | null;
    systemCacheTtl: "5m" | "1h" | null;
    systemCacheTtlSource: "instance" | "template" | null;
    /** Max input tokens of the effective model_ref — slider max is 90% of this. */
    contextWindowTokens: number;
  } | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlackboardChannel {
  id: string;
  displayName: string;
  kind: "private" | "shared";
  ownerActorId: string | null;
  provenance: "issue_workspace" | "manual" | "teams_bind";
  archivedAt?: string | null;
}

export interface ChannelMessage {
  id: string;
  channelId: string;
  senderType: "instance" | "user" | "system";
  senderId: string;
  senderDisplayName?: string | null;
  content: string;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Stream event types (SSE) — discriminated union by type
// ---------------------------------------------------------------------------

interface PhaseTransitionEvent {
  id: string;
  type: "phase_transition";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  from: string;
  to: string;
}

interface ToolCallEvent {
  id: string;
  type: "tool_call";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  tool: string;
  args?: string;
}

interface ChannelMessageEvent {
  id: string;
  type: "channel_message";
  timestamp: string;
  sourceInstanceId?: string;
  channelId: string;
  content: string;
  senderType: string;
}

interface StatusChangeEvent {
  id: string;
  type: "status_change";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  status: string;
}

interface BudgetWarningEvent {
  id: string;
  type: "budget_warning";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  message: string;
}

interface CacheStatsEvent {
  id: string;
  type: "cache_stats";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  cacheReadTokens: number;
  cacheHitPct: number | null;
  isBreak: boolean;
  reason?: string;
}

interface TokenDeltaEvent {
  id: string;
  type: "token_delta";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  inputTokensDelta: number;
  outputTokensDelta: number;
  cacheReadTokensDelta: number;
  cacheWriteTokensDelta: number;
  costDeltaUsd: number;
  modelRef?: string;
}

interface ActivitySignalEvent {
  id: string;
  type: "activity_signal";
  timestamp: string;
  sourceInstanceId?: string;
  channelId?: string;
  activityType: string;
  label: string;
  message: string;
}

export type StreamEvent =
  | PhaseTransitionEvent
  | ToolCallEvent
  | ChannelMessageEvent
  | StatusChangeEvent
  | BudgetWarningEvent
  | CacheStatsEvent
  | TokenDeltaEvent
  | ActivitySignalEvent;

// ---------------------------------------------------------------------------
// Status helpers
//
// Two layers:
//   1. `InstanceStatus` — durable lifecycle row in DB (`running`/`waiting`/`killed`).
//   2. `InstanceDisplayState` — what operators see. Folds in the in-flight LLM
//      step kind and the channel mutex wait state so the dashboard can
//      distinguish Queued / Generating / Compacting / Consolidating instead
//      of collapsing everything to "Acting".
//
// All UI surfaces that show actor activity (cards, drawers, previews) read
// from `getInstanceDisplayState` + `getInstanceDisplayStyle`. Raw Tailwind
// classes live here, not scattered in component files.
// ---------------------------------------------------------------------------

export type InstanceDisplayState =
  | "Acting"
  | "Compacting"
  | "Consolidating"
  | "Generating"
  | "Killed"
  | "Parked"
  | "Queued";

/**
 * Derive the operator-visible display state from persisted lifecycle, the
 * in-flight LLM step (from REST snapshot or WS updates), and an optional
 * live-generation hint (from token-stream TTL — finer-grained than persisted
 * step state during the gap between WS token activity and `llm_call_step` row).
 *
 * Priority:
 *   1. Durable terminal status → Parked / Killed
 *   2. Channel mutex waiting → Queued
 *   3. Persisted in-flight step → Generating / Acting / Compacting / Consolidating
 *   4. Live-generation hint (no persisted step yet) → Generating
 *   5. Fall through to durable running status → Acting
 */
export function getInstanceDisplayState(
  instance: Pick<
    BlackboardInstance,
    "channelLockWaiting" | "inFlightStepKind" | "inFlightWakeState" | "status"
  >,
  isGenerating = false,
): InstanceDisplayState {
  switch (instance.status) {
    case "waiting": {
      return "Parked";
    }
    case "killed": {
      return "Killed";
    }
    case "running": {
      break;
    }
    default: {
      throw assertUnreachable(instance.status);
    }
  }
  if (
    instance.channelLockWaiting ||
    instance.inFlightWakeState === "lock_waiting"
  ) {
    return "Queued";
  }
  switch (instance.inFlightStepKind) {
    case "actor_wake": {
      return instance.inFlightWakeState === "llm_call" || isGenerating
        ? "Generating"
        : "Acting";
    }
    case "external_compact_seed": {
      return "Compacting";
    }
    case "consolidation_only_wake": {
      return "Consolidating";
    }
    case null: {
      if (isGenerating) {
        return "Generating";
      }
      return "Acting";
    }
    default: {
      throw assertUnreachable(instance.inFlightStepKind);
    }
  }
}

export interface InstanceDisplayStyle {
  /** Foreground text class for the state label. */
  textClass: string;
  /** Animated dot class (`animate-* bg-*-500`); null when no pulse renders. */
  pulseClass: string | null;
  /** Hover tooltip explaining the state; null when label is self-explanatory. */
  tooltip: string | null;
}

const DISPLAY_STYLE: Record<InstanceDisplayState, InstanceDisplayStyle> = {
  Acting: {
    pulseClass: null,
    textClass: "text-slate-400 dark:text-slate-500",
    tooltip: null,
  },
  Compacting: {
    pulseClass: "animate-pulse bg-violet-500",
    textClass: "text-violet-600 dark:text-violet-400",
    tooltip:
      "Compacting — running an external compact seed pass to evolve summary + purge messages",
  },
  Consolidating: {
    pulseClass: "animate-pulse bg-violet-500",
    textClass: "text-violet-600 dark:text-violet-400",
    tooltip: "Consolidating — running a scheduled consolidation-only wake",
  },
  Generating: {
    pulseClass: "animate-ping bg-emerald-500",
    textClass: "text-emerald-600 dark:text-emerald-400",
    tooltip:
      "Generating — actor wake holding the channel mutex and emitting tokens",
  },
  Killed: {
    pulseClass: null,
    textClass: "text-slate-400 dark:text-slate-500",
    tooltip: null,
  },
  Parked: {
    pulseClass: null,
    textClass: "text-slate-400 dark:text-slate-500",
    tooltip: null,
  },
  Queued: {
    pulseClass: "animate-pulse bg-sky-500",
    textClass: "text-sky-600 dark:text-sky-400",
    tooltip:
      "Queued behind the per-channel mutex — another sibling is generating",
  },
};

export function getInstanceDisplayStyle(
  state: InstanceDisplayState,
): InstanceDisplayStyle {
  return DISPLAY_STYLE[state];
}

export function isWakeBlockActive(
  instance: Pick<BlackboardInstance, "wakeBlockedUntil">,
  nowMs = Date.now(),
): boolean {
  return (
    instance.wakeBlockedUntil !== null &&
    Date.parse(instance.wakeBlockedUntil) > nowMs
  );
}

// ---------------------------------------------------------------------------
// Wake-block presentation policy
//
// One chokepoint for every user-facing wake-block string and the affordance a
// surface is allowed to offer. Wizard, fleet cards, drawer header, and the
// detail block all read this; nobody re-switches on the reason code.
//
// Reason codes are OPEN STRINGS end-to-end (persistence writes a plain string,
// dashboard transport carries a plain string), so the unknown fallback is a
// production path, not a defensive branch: it must stay legible and must never
// promote the raw code to primary copy.
// ---------------------------------------------------------------------------

/** Affordance the owning surface may render. Navigation and authorization live
 * in the surface, so this model helper stays pure. */
export type WakeBlockActionKind =
  | "manage_credits"
  | "configure_provider"
  | "view_actor_details"
  | "none";

export interface WakeBlockPresentation {
  /** Trimmed reason code as produced, or null when absent. Diagnostics only. */
  reasonCode: string | null;
  /** Short label — the text after the "Blocked — " prefix. Never snake_case. */
  reasonLabel: string;
  /** Compact badge/callout heading shared by every surface. */
  badgeLabel: string;
  /** Non-truncated explanation. Parked input is always described as safe. */
  explanation: string;
  action: WakeBlockActionKind;
  /** True when Arti retries on its own and the surface should show the clock. */
  showsScheduledRetry: boolean;
  /** Copy to show instead of the CTA for users who cannot administer the org. */
  unauthorizedNote: string | null;
  /** True when the code was not recognized; `reasonCode` may be shown as
   * secondary diagnostic text. */
  isUnknownReason: boolean;
}

const UNKNOWN_WAKE_BLOCK_PRESENTATION: Omit<
  WakeBlockPresentation,
  "reasonCode"
> = {
  action: "none",
  badgeLabel: "Blocked — wake paused",
  explanation: "Pending input is safe and wake-up is currently paused.",
  isUnknownReason: true,
  reasonLabel: "wake paused",
  showsScheduledRetry: false,
  unauthorizedNote: null,
};

const KNOWN_WAKE_BLOCK_PRESENTATIONS: Record<
  string,
  Omit<WakeBlockPresentation, "badgeLabel" | "isUnknownReason" | "reasonCode">
> = {
  insufficient_credits: {
    action: "manage_credits",
    explanation:
      "This organization has no credits available. Add credits; the parked input will resume automatically.",
    reasonLabel: "credits required",
    showsScheduledRetry: false,
    unauthorizedNote: null,
  },
  non_retryable_prior_failure: {
    action: "view_actor_details",
    explanation:
      "A prior provider failure cannot be retried automatically. Pending input remains parked.",
    reasonLabel: "provider request needs attention",
    showsScheduledRetry: false,
    unauthorizedNote: null,
  },
  provider_configuration_missing: {
    action: "configure_provider",
    explanation:
      "This organization is missing the provider configuration required by this actor.",
    reasonLabel: "provider not configured",
    showsScheduledRetry: false,
    unauthorizedNote:
      "Ask an organization administrator to configure the provider.",
  },
  provider_retries_exhausted: {
    action: "view_actor_details",
    explanation:
      "The model provider kept failing. Pending input is parked and Arti will retry automatically.",
    reasonLabel: "provider retries exhausted",
    showsScheduledRetry: true,
    unauthorizedNote: null,
  },
  runtime_identity_missing: {
    action: "view_actor_details",
    explanation:
      "Arti cannot find this actor's runtime identity and is holding pending input safely.",
    reasonLabel: "actor setup incomplete",
    showsScheduledRetry: false,
    unauthorizedNote: null,
  },
  tool_executor_stalled: {
    action: "view_actor_details",
    explanation:
      "A tool execution stopped responding. Pending work is safe and Arti will retry automatically.",
    reasonLabel: "tool execution stalled",
    showsScheduledRetry: true,
    unauthorizedNote: null,
  },
};

/**
 * The single presentation policy for wake-block reason codes. Pure: no router,
 * no RBAC, no clock. Unknown or missing codes get legible primary copy plus
 * `reasonCode` for secondary diagnostics.
 */
export function wakeBlockPresentation(
  reason: string | null,
): WakeBlockPresentation {
  const normalizedReason = reason?.trim() ?? "";
  const reasonCode = normalizedReason === "" ? null : normalizedReason;
  const known = KNOWN_WAKE_BLOCK_PRESENTATIONS[normalizedReason];
  if (!known) {
    return { ...UNKNOWN_WAKE_BLOCK_PRESENTATION, reasonCode };
  }
  return {
    ...known,
    badgeLabel: `Blocked — ${known.reasonLabel}`,
    isUnknownReason: false,
    reasonCode,
  };
}

export function wakeBlockBadgeLabel(
  instance: Pick<
    BlackboardInstance,
    | "wakeBlockedConsecutiveFailureCount"
    | "wakeBlockedError"
    | "wakeBlockedReason"
    | "wakeBlockedUntil"
  >,
  nowMs = Date.now(),
): WakeBlockBadgeCopy | null {
  if (
    !isWakeBlockActive(instance, nowMs) ||
    instance.wakeBlockedUntil === null
  ) {
    return null;
  }
  const presentation = wakeBlockPresentation(instance.wakeBlockedReason);
  const { reasonLabel } = presentation;
  // Only reasons whose policy schedules a retry may claim one. The block has a
  // `wakeBlockedUntil` either way — it is when the *block* lapses, not a promise
  // that Arti will retry then — so a credits/config block must stay silent.
  const retryClock = presentation.showsScheduledRetry
    ? formatRetryClock(instance.wakeBlockedUntil)
    : null;
  const errorPreview = wakeBlockedErrorPreview(instance.wakeBlockedError);
  const failureCount = normalizeFailureCount(
    instance.wakeBlockedConsecutiveFailureCount,
  );
  const failureTitle =
    failureCount !== null && failureCount > 1
      ? `${failureCount} consecutive failures`
      : null;
  const title = [
    reasonLabel,
    retryClock === null ? null : `retrying ${retryClock}`,
    failureTitle,
    errorPreview,
  ]
    .filter((part): part is string => part !== null)
    .join(" · ");

  return {
    errorPreview,
    failureCount,
    label: presentation.badgeLabel,
    presentation,
    reasonLabel,
    retryClock,
    retryTime: presentation.showsScheduledRetry
      ? formatRetryTime(instance.wakeBlockedUntil)
      : null,
    title,
  };
}

export interface WakeBlockBadgeCopy {
  label: string;
  title: string;
  reasonLabel: string;
  /** Null unless the policy schedules a retry for this reason code. */
  retryClock: string | null;
  /** Null unless the policy schedules a retry for this reason code. */
  retryTime: string | null;
  errorPreview: string | null;
  failureCount: number | null;
  /** Shared policy output, so detail surfaces add explanation/affordance
   * without re-switching on the reason code. */
  presentation: WakeBlockPresentation;
}

export function wakeBlockedErrorPreview(error: string | null): string | null {
  const trimmedError = error?.trim();
  if (!trimmedError) {
    return null;
  }
  return (
    extractJsonMessage(trimmedError) ?? firstUsefulErrorFragment(trimmedError)
  );
}

function normalizeFailureCount(count: number | null): number | null {
  return count !== null && Number.isFinite(count) && count > 0
    ? Math.trunc(count)
    : null;
}

function extractJsonMessage(error: string): string | null {
  const directMessage = parseJsonMessage(error);
  if (directMessage !== null) {
    return directMessage;
  }

  const jsonStart = error.indexOf("{");
  const jsonEnd = error.lastIndexOf("}");
  if (jsonStart === -1 || jsonEnd <= jsonStart) {
    return null;
  }
  return parseJsonMessage(error.slice(jsonStart, jsonEnd + 1));
}

function parseJsonMessage(json: string): string | null {
  const parsed = parseJson(json);
  return parsed === null || parsed === undefined
    ? null
    : readMessageFromJson(parsed);
}

function readMessageFromJson(value: unknown): string | null {
  if (typeof value === "string") {
    return firstUsefulErrorFragment(value);
  }

  if (Array.isArray(value)) {
    return value.map(readMessageFromJson).find(hasStringValue) ?? null;
  }

  const directMessage = readStringProperty(value, "message");
  if (directMessage !== null) {
    return firstUsefulErrorFragment(directMessage);
  }

  return (
    readMessageProperty(value, "error") ??
    readMessageProperty(value, "details") ??
    readMessageProperty(value, "detail")
  );
}

function readMessageProperty(value: unknown, key: string): string | null {
  if (!hasProperty(value, key)) {
    return null;
  }
  return readMessageFromJson(value[key]);
}

function readStringProperty(value: unknown, key: string): string | null {
  if (!hasProperty(value, key)) {
    return null;
  }
  return typeof value[key] === "string" ? value[key] : null;
}

function hasProperty<K extends string>(
  value: unknown,
  key: K,
): value is Record<K, unknown> {
  return typeof value === "object" && value !== null && key in value;
}

function hasStringValue(value: string | null): value is string {
  return value !== null;
}

function firstUsefulErrorFragment(error: string): string | null {
  const firstLine = error
    .replace(/\r\n/gu, "\n")
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (!firstLine) {
    return null;
  }
  const firstSentence = firstLine.match(/^(.+?[.!?])(?:\s|$)/u)?.[1];
  return truncateErrorPreview(firstSentence ?? firstLine);
}

function truncateErrorPreview(error: string): string {
  const maxLength = 220;
  const normalizedError = error.replace(/\s+/gu, " ").trim();
  if (normalizedError.length <= maxLength) {
    return normalizedError;
  }
  return `${normalizedError.slice(0, maxLength - 1).trimEnd()}…`;
}

function formatRetryClock(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) {
    return "soon";
  }
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
  });
}

function formatRetryTime(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  if (!Number.isFinite(date.getTime())) {
    return "soon";
  }
  return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Status → semantic color name. Stable hue per durable lifecycle state,
 * independent of in-flight activity. Used for accent bars, avatar rings,
 * and other "what kind of actor row is this" chrome.
 */
export function getInstanceStatusColor(
  status: InstanceStatus,
): "emerald" | "amber" | "rose" {
  switch (status) {
    case "running": {
      return "emerald";
    }
    case "waiting": {
      return "amber";
    }
    case "killed": {
      return "rose";
    }
    default: {
      throw assertUnreachable(status);
    }
  }
}

const STATUS_ACCENT_BAR_CLASS: Record<
  ReturnType<typeof getInstanceStatusColor>,
  string
> = { amber: "bg-amber-400", emerald: "bg-emerald-500", rose: "bg-rose-500" };

/** Tailwind `bg-{color}-{shade}` for the thin accent stripe at the top of a card. */
export function getInstanceStatusAccentBarClass(
  status: InstanceStatus,
): string {
  return STATUS_ACCENT_BAR_CLASS[getInstanceStatusColor(status)];
}

// ---------------------------------------------------------------------------
// Cache-tone tokens — used by InstanceCard's CacheHitChip + ThermalIndicator.
// Bespoke palette (sky for cold, amber for warm, emerald for hot) that bypasses
// the canonical colorTokens.ts because that palette aliases sky→slate; here we
// want the actual sky hue so cold visually reads as "cool". Keep these strings
// next to the state semantics, not in the component body.
// ---------------------------------------------------------------------------

export type ThermalTone = "hot" | "warm" | "cold";

export interface ThermalToneStyle {
  /** Foreground text class for the badge label. */
  textClass: string;
  /** Glyph rendered with the label. */
  glyph: string;
  /** Short noun for tooltips (e.g. "Hot"). */
  label: string;
}

const THERMAL_TONE_STYLE: Record<ThermalTone, ThermalToneStyle> = {
  cold: {
    glyph: "❄",
    label: "Cold",
    textClass: "text-sky-600 dark:text-sky-400",
  },
  hot: {
    glyph: "▲",
    label: "Hot",
    textClass: "text-emerald-600 dark:text-emerald-400",
  },
  warm: {
    glyph: "●",
    label: "Warm",
    textClass: "text-amber-600 dark:text-amber-400",
  },
};

export function getThermalToneStyle(tone: ThermalTone): ThermalToneStyle {
  return THERMAL_TONE_STYLE[tone];
}

/**
 * Cache-hit drift colors. The chip itself stays emerald (matches "good cache
 * health"); the drift arrow flips to amber when the last activation dropped
 * by >= DRIFT_THRESHOLD_PP relative to lifetime.
 */
export const CACHE_HIT_TEXT_CLASS = "text-emerald-600 dark:text-emerald-400";
export const CACHE_HIT_DRIFT_DOWN_CLASS = "text-amber-500 dark:text-amber-400";
export const CACHE_HIT_DRIFT_UP_CLASS =
  "text-emerald-500 dark:text-emerald-400";

/** Run-count warning chip — amber to match the "restarts" warning semantic. */
export const RUN_COUNT_WARNING_TEXT_CLASS =
  "text-amber-500 dark:text-amber-400";
